import { useGLTF, useTexture } from "@react-three/drei";
import React, { useLayoutEffect, useRef } from "react";
import { BufferGeometry, Texture } from "three";
import { useStep } from "../../../context/stepContext";
import { Step } from "../steps/Step";
import * as THREE from "three";

type ChairProps = {
    enableClick: boolean;
    url: string;
    cover: string;
    frame: string;
    secondary?: string;
    repeat: number;
    shininess: {
        cover: number;
        frame: number;
        secondary?: number;
    };
};

type TextureProps = {
    url: string;
    shininess: number;
    repeat: number | undefined;
};

type MeshProps = {
    enableClick: boolean;
    geometry: BufferGeometry;
    id: string;
    cover: string;
    frame: string;
    secondary?: string;
    repeat: number;
    shininess: {
        cover: number;
        frame: number;
        secondary?: number;
    };
};

const TextureMesh = (props: TextureProps) => {
    const texture = useTexture(props.url);

    useLayoutEffect(() => {
        texture.repeat.set(props.repeat ?? 1, props.repeat ?? 1);
        texture.wrapT = THREE.RepeatWrapping;
        texture.wrapS = THREE.RepeatWrapping;

        texture.colorSpace = THREE.SRGBColorSpace;

        texture.needsUpdate = true;
    }, [texture, props.repeat]);

    const getSpecular = () => {
        if (props.shininess < 10) {
            return 0x111111;
        } else if (props.shininess < 20) {
            return 0x121212;
        } else if (props.shininess < 30) {
            return 0x222222;
        } else if (props.shininess < 40) {
            return 0x232323;
        } else if (props.shininess < 50) {
            return 0x333333;
        } else if (props.shininess < 60) {
            return 0x343434;
        } else if (props.shininess < 70) {
            return 0x444444;
        } else if (props.shininess < 80) {
            return 0x454545;
        } else if (props.shininess < 90) {
            return 0x555555;
        } else {
            return 0x565656;
        }
    };

    return <meshPhongMaterial map={texture as Texture} shininess={props.shininess} specular={getSpecular()} />;
};

const Mesh = (props: MeshProps) => {
    const { setStep } = useStep();
    const id = props.id;
    const startPos = useRef<{ x: number; y: number } | null>(null);

    const getMaterial = () => {
        if (id.includes("Bezug") && props.cover) {
            return { url: props.cover, shininess: props.shininess.cover, repeat: props.repeat };
        } else if (id.includes("Secondary") && props.secondary) {
            return { url: props.secondary, shininess: props.shininess.secondary, repeat: 1 };
        } else if (((id.includes("Frame") && !id.includes("Secondary")) || id.includes("Knopf")) && props.frame) {
            return { url: props.frame, shininess: props.shininess.frame, repeat: 1 };
        }
        return null;
    };
    const materialConfig: { url: string; shininess: number; repeat: number | undefined } | null = getMaterial();

    return (
        <mesh
            receiveShadow={true}
            castShadow={true}
            geometry={props.geometry}
            position={[0, 0, 0]}
            onPointerDown={(e) => {
                startPos.current = { x: e.clientX, y: e.clientY };
            }}
            onPointerUp={(e) => {
                if (props.enableClick) {
                    e.stopPropagation();
                    if (
                        startPos.current &&
                        Math.abs(startPos.current.x - e.clientX) < 5 &&
                        Math.abs(startPos.current.y - e.clientY) < 5
                    ) {
                        if (id.includes("Bezug")) {
                            setStep(Step.COVER);
                        } else if (id.includes("Frame") && !id.includes("Secondary")) {
                            setStep(Step.FRAME);
                        }
                    }
                }
            }}
        >
            {materialConfig ? (
                <TextureMesh
                    url={materialConfig.url}
                    shininess={materialConfig.shininess}
                    repeat={materialConfig.repeat}
                />
            ) : (
                <meshPhongMaterial />
            )}
        </mesh>
    );
};
export const Chair = (props: ChairProps) => {
    // @ts-ignore useGLTF returns nodes
    const { nodes } = useGLTF(props.url ?? "");

    return (
        <group dispose={null}>
            {Object.keys(nodes).map((key) => {
                // @ts-ignore we need the geometry to continue, we don't add the mesh if there is no geometry
                const geometry = nodes[key].geometry ?? null;
                return geometry ? (
                    <Mesh
                        key={key}
                        geometry={geometry}
                        id={key}
                        cover={props.cover}
                        frame={props.frame}
                        secondary={props.secondary}
                        repeat={props.repeat}
                        shininess={props.shininess}
                        enableClick={props.enableClick}
                    />
                ) : null;
            })}
        </group>
    );
};
